import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCSDStatus,
  getFacturas,
  crearFactura,
  cancelarFactura,
  descargarFacturaPDF,
  descargarFacturaXML,
  enviarFacturaEmail,
  getRegimenesFiscales,
  getUsosCFDI,
  getFormasPago,
  getMetodosPago,
  getBusiness,
  getClients,
  getProducts,
  buscarProductosSAT,
  buscarUnidadesSAT
} from '../../services/api';
import {
  FileText,
  CheckCircle,
  XCircle,
  Download,
  Mail,
  AlertCircle,
  Loader2,
  RefreshCw,
  Eye,
  Ban,
  Search,
  UserCheck,
  Plus,
  Trash2,
  Copy,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Send,
  Package,
  X,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

// ==================== Helpers ====================

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const emptyConcepto = () => ({
  _key: Date.now() + Math.random(),
  descripcion: '',
  cantidad: 1,
  valor_unitario: 0,
  clave_prod_serv: '01010101',
  clave_unidad: 'H87',
  unidad: 'Pieza',
  no_identificacion: '',
  descuento: 0,
  tasa_iva: 0.16,
  iva_exento: false,
  objeto_imp: '02',
  tasa_ieps: 0,
  tasa_ret_iva: 0,
  tasa_ret_isr: 0,
  _showTaxes: false,
  _satSearch: '',
  _satResults: [],
  _unitSearch: '',
  _unitResults: [],
});

// ==================== Main Component ====================

const FacturacionManual = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | form
  const [csdStatus, setCsdStatus] = useState(null);
  const [business, setBusiness] = useState(null);

  // Catalogs
  const [regimenes, setRegimenes] = useState([]);
  const [usosCFDI, setUsosCFDI] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);

  // List view
  const [facturas, setFacturas] = useState([]);
  const [listFilter, setListFilter] = useState('todas'); // todas | vigentes | canceladas
  const [listSearch, setListSearch] = useState('');
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const [downloadingXML, setDownloadingXML] = useState(null);
  const [selectedFactura, setSelectedFactura] = useState(null);

  // Form state
  const [serie, setSerie] = useState('M');
  const [folio, setFolio] = useState('');
  const [receptor, setReceptor] = useState({
    rfc: '', nombre: '', regimen_fiscal: '601',
    uso_cfdi: 'G03', domicilio_fiscal: '', email: ''
  });
  const [conceptos, setConceptos] = useState([emptyConcepto()]);
  const [formaPago, setFormaPago] = useState('03');
  const [metodoPago, setMetodoPago] = useState('PUE');
  const [condicionesPago, setCondicionesPago] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [timbrado, setTimbrado] = useState(false);
  const [timbrando, setTimbrando] = useState(false);
  const [resultadoTimbrado, setResultadoTimbrado] = useState(null);

  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // ==================== Load data ====================

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [csdRes, facturasRes, businessRes] = await Promise.all([
        getCSDStatus(),
        getFacturas({ limit: 200 }),
        getBusiness(user.business_id)
      ]);
      setCsdStatus(csdRes.data);
      setFacturas(facturasRes.data);
      setBusiness(businessRes.data);

      const [regRes, usosRes, formasRes, metodosRes] = await Promise.all([
        getRegimenesFiscales(),
        getUsosCFDI(),
        getFormasPago(),
        getMetodosPago()
      ]);
      setRegimenes(regRes.data);
      setUsosCFDI(usosRes.data);
      setFormasPago(formasRes.data);
      setMetodosPago(metodosRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos de facturación');
    } finally {
      setLoading(false);
    }
  };

  // ==================== Client search (debounced) ====================

  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2 || !user?.business_id) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await getClients(user.business_id, { q: clientSearch });
        setClientResults(res.data.slice(0, 10));
        setShowClientDropdown(res.data.length > 0);
      } catch (e) {
        console.error('Error searching clients:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, user?.business_id]);

  const handleSelectClient = (client) => {
    setReceptor({
      rfc: client.rfc,
      nombre: client.razon_social,
      regimen_fiscal: client.regimen_fiscal || '601',
      uso_cfdi: client.uso_cfdi || 'G03',
      domicilio_fiscal: client.domicilio_fiscal || '',
      email: client.email || ''
    });
    setClientSearch('');
    setClientResults([]);
    setShowClientDropdown(false);
  };

  // ==================== Product search (debounced) ====================

  useEffect(() => {
    if (!productSearch || productSearch.length < 2) {
      setProductResults([]);
      setShowProductDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await getProducts({ business_id: user.business_id, search: productSearch, limit: 10 });
        setProductResults(res.data.products || res.data || []);
        setShowProductDropdown(true);
      } catch (e) {
        console.error('Error searching products:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, user?.business_id]);

  const handleSelectProduct = (product) => {
    const tax = product.tax_config || {};
    const newConcepto = {
      ...emptyConcepto(),
      descripcion: product.name,
      valor_unitario: product.pricing?.retail_price || 0,
      clave_prod_serv: product.sat_code || '01010101',
      clave_unidad: product.sat_unit || 'H87',
      unidad: product.sat_unit_name || 'Pieza',
      no_identificacion: product.sku || '',
      tasa_iva: tax.iva_exempt ? 0 : (tax.iva_rate ?? 0.16),
      iva_exento: tax.iva_exempt || false,
      objeto_imp: tax.tax_object || '02',
      tasa_ieps: tax.ieps_rate || 0,
      tasa_ret_iva: tax.retention_iva_rate || 0,
      tasa_ret_isr: tax.retention_isr_rate || 0,
    };
    setConceptos(prev => [...prev, newConcepto]);
    setProductSearch('');
    setProductResults([]);
    setShowProductDropdown(false);
  };

  // ==================== Concepto handlers ====================

  const updateConcepto = (index, field, value) => {
    setConceptos(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeConcepto = (index) => {
    setConceptos(prev => prev.filter((_, i) => i !== index));
  };

  const duplicateConcepto = (index) => {
    setConceptos(prev => {
      const dup = { ...prev[index], _key: Date.now() + Math.random() };
      const copy = [...prev];
      copy.splice(index + 1, 0, dup);
      return copy;
    });
  };

  const toggleTaxes = (index) => {
    updateConcepto(index, '_showTaxes', !conceptos[index]._showTaxes);
  };

  // ==================== SAT catalog search for concepto ====================

  const handleSATSearch = useCallback(async (index, query) => {
    updateConcepto(index, '_satSearch', query);
    if (query.length < 3) {
      updateConcepto(index, '_satResults', []);
      return;
    }
    try {
      const res = await buscarProductosSAT(query);
      updateConcepto(index, '_satResults', (res.data || []).slice(0, 8));
    } catch (e) {
      console.error('SAT search error:', e);
    }
  }, []);

  const handleUnitSearch = useCallback(async (index, query) => {
    updateConcepto(index, '_unitSearch', query);
    if (query.length < 2) {
      updateConcepto(index, '_unitResults', []);
      return;
    }
    try {
      const res = await buscarUnidadesSAT(query);
      updateConcepto(index, '_unitResults', (res.data || []).slice(0, 8));
    } catch (e) {
      console.error('Unit search error:', e);
    }
  }, []);

  // ==================== Totals calculation ====================

  const totals = useMemo(() => {
    let subtotal = 0, descuento = 0, iva = 0, ieps = 0, retIva = 0, retIsr = 0;
    conceptos.forEach(c => {
      const importe = c.cantidad * c.valor_unitario;
      const desc = c.descuento || 0;
      const base = importe - desc;
      subtotal += importe;
      descuento += desc;
      if (!c.iva_exento) iva += base * (c.tasa_iva || 0);
      ieps += base * (c.tasa_ieps || 0);
      retIva += base * (c.tasa_ret_iva || 0);
      retIsr += base * (c.tasa_ret_isr || 0);
    });
    const total = subtotal - descuento + iva + ieps - retIva - retIsr;
    return { subtotal, descuento, iva, ieps, retIva, retIsr, total };
  }, [conceptos]);

  // ==================== Submit ====================

  const handleTimbrar = async (sendEmail = false) => {
    // Validation
    if (!receptor.rfc || !receptor.nombre || !receptor.domicilio_fiscal || !receptor.regimen_fiscal) {
      toast.error('Completa todos los campos obligatorios del receptor');
      return;
    }
    const validConceptos = conceptos.filter(c => c.descripcion && c.valor_unitario > 0);
    if (validConceptos.length === 0) {
      toast.error('Agrega al menos un concepto con descripción y precio');
      return;
    }

    setTimbrando(true);
    try {
      const payload = {
        receptor: {
          rfc: receptor.rfc.toUpperCase(),
          nombre: receptor.nombre.toUpperCase(),
          regimen_fiscal: receptor.regimen_fiscal,
          uso_cfdi: receptor.uso_cfdi,
          domicilio_fiscal: receptor.domicilio_fiscal
        },
        conceptos: validConceptos.map(c => ({
          descripcion: c.descripcion,
          cantidad: c.cantidad,
          valor_unitario: c.valor_unitario,
          clave_prod_serv: c.clave_prod_serv,
          clave_unidad: c.clave_unidad,
          unidad: c.unidad,
          no_identificacion: c.no_identificacion || null,
          descuento: c.descuento || 0,
          tasa_iva: c.tasa_iva,
          iva_exento: c.iva_exento,
          objeto_imp: c.objeto_imp,
          tasa_ieps: c.tasa_ieps,
          tasa_ret_iva: c.tasa_ret_iva,
          tasa_ret_isr: c.tasa_ret_isr,
        })),
        forma_pago: formaPago,
        metodo_pago: metodoPago,
        serie: serie || 'M',
        folio: folio || null,
        condiciones_pago: condicionesPago || null,
        observaciones: observaciones || null
      };

      const res = await crearFactura(payload);
      const result = res.data;
      setResultadoTimbrado(result);
      setTimbrado(true);
      toast.success('Factura timbrada exitosamente');

      // Send email if requested
      if (sendEmail && receptor.email && result.invoice_id) {
        try {
          await enviarFacturaEmail(result.invoice_id, { email: receptor.email });
          toast.success(`Factura enviada a ${receptor.email}`);
        } catch (emailErr) {
          toast.error('La factura se timbró pero hubo error al enviar el email');
        }
      }

      // Reload invoices
      const facturasRes = await getFacturas({ limit: 200 });
      setFacturas(facturasRes.data);
    } catch (error) {
      console.error('Error al timbrar:', error);
      toast.error(getErrorMessage(error, 'Error al timbrar factura'));
    } finally {
      setTimbrando(false);
    }
  };

  // ==================== Invoice actions ====================

  const handleDescargarPDF = async (factura) => {
    const id = factura.id || factura.invoice_id;
    setDownloadingPDF(id);
    try {
      const response = await descargarFacturaPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${factura.serie || ''}${factura.folio || ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al descargar PDF'));
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handleDescargarXML = async (factura) => {
    const id = factura.id || factura.invoice_id;
    setDownloadingXML(id);
    try {
      const response = await descargarFacturaXML(id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${factura.serie || ''}${factura.folio || ''}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('XML descargado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al descargar XML'));
    } finally {
      setDownloadingXML(null);
    }
  };

  const handleCancelarFactura = async (facturaId, motivo) => {
    try {
      await cancelarFactura(facturaId, { motivo });
      toast.success('Factura cancelada exitosamente');
      setSelectedFactura(null);
      const facturasRes = await getFacturas({ limit: 200 });
      setFacturas(facturasRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cancelar factura'));
    }
  };

  const handleEnviarEmail = async (facturaId, email) => {
    try {
      await enviarFacturaEmail(facturaId, { email });
      toast.success(`Factura enviada a ${email}`);
    } catch (error) {
      toast.error('Error al enviar factura por email');
    }
  };

  // ==================== Reset form ====================

  const resetForm = () => {
    setReceptor({ rfc: '', nombre: '', regimen_fiscal: '601', uso_cfdi: 'G03', domicilio_fiscal: '', email: '' });
    setConceptos([emptyConcepto()]);
    setSerie('M');
    setFolio('');
    setFormaPago('03');
    setMetodoPago('PUE');
    setCondicionesPago('');
    setObservaciones('');
    setTimbrado(false);
    setResultadoTimbrado(null);
    setClientSearch('');
    setProductSearch('');
  };

  // ==================== Filtered invoices ====================

  const filteredFacturas = useMemo(() => {
    let list = facturas;
    if (listFilter === 'vigentes') list = list.filter(f => f.estatus === 'vigente');
    if (listFilter === 'canceladas') list = list.filter(f => f.estatus === 'cancelada');
    if (listSearch) {
      const q = listSearch.toLowerCase();
      list = list.filter(f =>
        (f.serie + f.folio).toLowerCase().includes(q) ||
        (f.receptor_rfc || '').toLowerCase().includes(q) ||
        (f.receptor_nombre || '').toLowerCase().includes(q) ||
        (f.uuid || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [facturas, listFilter, listSearch]);

  // ==================== Emisor info ====================

  const emisor = useMemo(() => {
    if (!business) return null;
    return {
      rfc: business.facturama_rfc || '',
      nombre: business.facturama_razon_social || business.name || '',
      regimen: business.regimen_fiscal || '601',
      cp: business.facturama_codigo_postal || '',
    };
  }, [business]);

  const emisorIncompleto = emisor && (!emisor.rfc || !emisor.nombre || !emisor.cp);

  // ==================== Render ====================

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            {view === 'form' && (
              <button onClick={() => { setView('list'); resetForm(); }}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2">
                <ArrowLeft className="w-4 h-4" /> Volver a la lista
              </button>
            )}
            <h1 className="text-2xl font-bold text-slate-900">
              {view === 'form' ? (timbrado ? 'Factura Timbrada' : 'Nueva Factura Manual') : 'Facturación Manual'}
            </h1>
            <p className="text-slate-500">
              {view === 'form'
                ? 'CFDI 4.0 - Factura de Ingreso'
                : 'Crea facturas manuales CFDI 4.0'}
            </p>
          </div>
          <div className="flex gap-2">
            {view === 'list' && (
              <>
                <button onClick={loadData}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                  <RefreshCw className="w-4 h-4" /> Actualizar
                </button>
                <button
                  onClick={() => { resetForm(); setView('form'); }}
                  disabled={!csdStatus?.registered}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus className="w-4 h-4" /> Nueva Factura
                </button>
              </>
            )}
          </div>
        </div>

        {/* CSD Warning */}
        {!csdStatus?.registered && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Certificado Digital no registrado</p>
              <p className="text-sm text-amber-700 mt-1">
                Registra tu CSD en la sección de <a href="/dashboard/facturacion" className="underline font-medium">Facturación de Tickets</a> para poder emitir facturas.
              </p>
            </div>
          </div>
        )}

        {view === 'list' ? (
          <ListView
            facturas={filteredFacturas}
            listFilter={listFilter}
            setListFilter={setListFilter}
            listSearch={listSearch}
            setListSearch={setListSearch}
            selectedFactura={selectedFactura}
            setSelectedFactura={setSelectedFactura}
            downloadingPDF={downloadingPDF}
            downloadingXML={downloadingXML}
            onDescargarPDF={handleDescargarPDF}
            onDescargarXML={handleDescargarXML}
            onCancelar={handleCancelarFactura}
            onEnviarEmail={handleEnviarEmail}
          />
        ) : timbrado && resultadoTimbrado ? (
          <PostTimbradorView
            resultado={resultadoTimbrado}
            onDescargarPDF={() => handleDescargarPDF(resultadoTimbrado)}
            onDescargarXML={() => handleDescargarXML(resultadoTimbrado)}
            downloadingPDF={downloadingPDF}
            downloadingXML={downloadingXML}
            email={receptor.email}
            onEnviarEmail={(email) => handleEnviarEmail(resultadoTimbrado.invoice_id, email)}
            onNuevaFactura={() => { resetForm(); }}
            onVolverLista={() => { setView('list'); resetForm(); }}
          />
        ) : (
          <FormView
            emisor={emisor}
            emisorIncompleto={emisorIncompleto}
            receptor={receptor}
            setReceptor={setReceptor}
            clientSearch={clientSearch}
            setClientSearch={setClientSearch}
            clientResults={clientResults}
            showClientDropdown={showClientDropdown}
            setShowClientDropdown={setShowClientDropdown}
            onSelectClient={handleSelectClient}
            conceptos={conceptos}
            setConceptos={setConceptos}
            updateConcepto={updateConcepto}
            removeConcepto={removeConcepto}
            duplicateConcepto={duplicateConcepto}
            toggleTaxes={toggleTaxes}
            onSATSearch={handleSATSearch}
            onUnitSearch={handleUnitSearch}
            productSearch={productSearch}
            setProductSearch={setProductSearch}
            productResults={productResults}
            showProductDropdown={showProductDropdown}
            setShowProductDropdown={setShowProductDropdown}
            onSelectProduct={handleSelectProduct}
            serie={serie}
            setSerie={setSerie}
            folio={folio}
            setFolio={setFolio}
            formaPago={formaPago}
            setFormaPago={setFormaPago}
            metodoPago={metodoPago}
            setMetodoPago={setMetodoPago}
            condicionesPago={condicionesPago}
            setCondicionesPago={setCondicionesPago}
            observaciones={observaciones}
            setObservaciones={setObservaciones}
            formasPago={formasPago}
            metodosPago={metodosPago}
            regimenes={regimenes}
            usosCFDI={usosCFDI}
            totals={totals}
            timbrando={timbrando}
            onTimbrar={handleTimbrar}
            onCancelar={() => { setView('list'); resetForm(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

// ==================== List View ====================

const ListView = ({
  facturas, listFilter, setListFilter, listSearch, setListSearch,
  selectedFactura, setSelectedFactura,
  downloadingPDF, downloadingXML,
  onDescargarPDF, onDescargarXML, onCancelar, onEnviarEmail
}) => {
  return (
    <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[
            { key: 'todas', label: 'Todas' },
            { key: 'vigentes', label: 'Vigentes' },
            { key: 'canceladas', label: 'Canceladas' },
          ].map(f => (
            <button key={f.key}
              onClick={() => setListFilter(f.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                listFilter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
            placeholder="Buscar por folio, RFC, nombre o UUID..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {facturas.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay facturas {listSearch ? 'con ese criterio' : ''}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Serie/Folio</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Receptor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">RFC</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Estatus</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {facturas.map((factura) => (
                  <tr key={factura.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{factura.serie}{factura.folio}</td>
                    <td className="px-4 py-3 text-slate-600 text-sm">{formatDate(factura.fecha || factura.created_at)}</td>
                    <td className="px-4 py-3 text-slate-900 text-sm">{factura.receptor_nombre}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-sm">{factura.receptor_rfc}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{formatCurrency(factura.total)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        factura.estatus === 'vigente' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {factura.estatus === 'vigente' ? 'Vigente' : 'Cancelada'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => onDescargarPDF(factura)} disabled={downloadingPDF === factura.id}
                          className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50" title="PDF">
                          {downloadingPDF === factura.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        </button>
                        <button onClick={() => onDescargarXML(factura)} disabled={downloadingXML === factura.id}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50" title="XML">
                          {downloadingXML === factura.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setSelectedFactura(factura)}
                          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Detalle">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedFactura && (
        <DetalleModal
          factura={selectedFactura}
          onClose={() => setSelectedFactura(null)}
          onDescargarPDF={() => onDescargarPDF(selectedFactura)}
          onDescargarXML={() => onDescargarXML(selectedFactura)}
          downloadingPDF={downloadingPDF === selectedFactura.id}
          downloadingXML={downloadingXML === selectedFactura.id}
          onCancelar={(motivo) => onCancelar(selectedFactura.id, motivo)}
          onEnviarEmail={(email) => onEnviarEmail(selectedFactura.id, email)}
        />
      )}
    </>
  );
};

// ==================== Detail Modal ====================

const DetalleModal = ({ factura, onClose, onCancelar, onEnviarEmail, onDescargarPDF, onDescargarXML, downloadingPDF, downloadingXML }) => {
  const [showCancelar, setShowCancelar] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('02');
  const [emailEnvio, setEmailEnvio] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!emailEnvio) return;
    setEnviando(true);
    await onEnviarEmail(emailEnvio);
    setEnviando(false);
    setEmailEnvio('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-900">Factura {factura.serie}{factura.folio}</h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            factura.estatus === 'vigente' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {factura.estatus === 'vigente' ? 'Vigente' : 'Cancelada'}
          </span>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">UUID (Folio Fiscal)</p>
            <p className="font-mono text-sm text-slate-900 break-all">{factura.uuid || 'N/A'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-1">Emisor</p>
              <p className="font-medium text-slate-900">{factura.emisor_nombre}</p>
              <p className="text-sm text-slate-600 font-mono">{factura.emisor_rfc}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Receptor</p>
              <p className="font-medium text-slate-900">{factura.receptor_nombre}</p>
              <p className="text-sm text-slate-600 font-mono">{factura.receptor_rfc}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Fecha</p>
              <p className="text-slate-900">{formatDate(factura.fecha || factura.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Método de Pago</p>
              <p className="text-slate-900">{factura.metodo_pago} / {factura.forma_pago}</p>
            </div>
          </div>
          <div className="bg-teal-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-teal-700">Subtotal:</span>
              <span className="text-teal-900">{formatCurrency(factura.subtotal)}</span>
            </div>
            {(factura.descuento > 0) && (
              <div className="flex justify-between text-sm">
                <span className="text-teal-700">Descuento:</span>
                <span className="text-teal-900">-{formatCurrency(factura.descuento)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-teal-700">IVA:</span>
              <span className="text-teal-900">{formatCurrency(factura.iva)}</span>
            </div>
            {(factura.ieps > 0) && (
              <div className="flex justify-between text-sm">
                <span className="text-teal-700">IEPS:</span>
                <span className="text-teal-900">{formatCurrency(factura.ieps)}</span>
              </div>
            )}
            {(factura.retencion_iva > 0) && (
              <div className="flex justify-between text-sm">
                <span className="text-teal-700">Ret. IVA:</span>
                <span className="text-teal-900">-{formatCurrency(factura.retencion_iva)}</span>
              </div>
            )}
            {(factura.retencion_isr > 0) && (
              <div className="flex justify-between text-sm">
                <span className="text-teal-700">Ret. ISR:</span>
                <span className="text-teal-900">-{formatCurrency(factura.retencion_isr)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-teal-200">
              <span className="text-teal-900">Total:</span>
              <span className="text-teal-900">{formatCurrency(factura.total)}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={onDescargarPDF} disabled={downloadingPDF}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50">
              {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloadingPDF ? 'Generando...' : 'PDF'}
            </button>
            <button onClick={onDescargarXML} disabled={downloadingXML}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              {downloadingXML ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {downloadingXML ? 'Descargando...' : 'XML'}
            </button>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Enviar por email</label>
            <div className="flex gap-2">
              <input type="email" value={emailEnvio} onChange={(e) => setEmailEnvio(e.target.value)}
                placeholder="correo@ejemplo.com"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
              <button onClick={handleEnviar} disabled={!emailEnvio || enviando}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enviar
              </button>
            </div>
          </div>
          {factura.estatus === 'vigente' && (
            <div className="border-t border-slate-200 pt-4">
              {!showCancelar ? (
                <button onClick={() => setShowCancelar(true)} className="flex items-center gap-2 text-red-600 hover:text-red-700">
                  <Ban className="w-4 h-4" /> Cancelar Factura
                </button>
              ) : (
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-red-800 font-medium">¿Cancelar esta factura?</p>
                  <select value={motivoCancelacion} onChange={(e) => setMotivoCancelacion(e.target.value)}
                    className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm">
                    <option value="01">01 - Con errores con relación</option>
                    <option value="02">02 - Con errores sin relación</option>
                    <option value="03">03 - No se llevó a cabo</option>
                    <option value="04">04 - Nominativa con factura global</option>
                  </select>
                  <div className="flex gap-2">
                    <button onClick={() => setShowCancelar(false)}
                      className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">No</button>
                    <button onClick={() => onCancelar(motivoCancelacion)}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Sí, cancelar</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cerrar</button>
        </div>
      </div>
    </div>
  );
};

// ==================== Post-Timbrado View ====================

const PostTimbradorView = ({ resultado, onDescargarPDF, onDescargarXML, downloadingPDF, downloadingXML, email, onEnviarEmail, onNuevaFactura, onVolverLista }) => {
  const [emailEnvio, setEmailEnvio] = useState(email || '');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!emailEnvio) return;
    setEnviando(true);
    await onEnviarEmail(emailEnvio);
    setEnviando(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-8 text-center bg-green-50 border-b border-green-200">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Factura Timbrada</h2>
          <p className="text-slate-600">La factura ha sido creada y timbrada exitosamente</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Serie / Folio</p>
              <p className="font-medium text-slate-900">{resultado.serie}{resultado.folio}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Total</p>
              <p className="font-medium text-slate-900">{formatCurrency(resultado.total)}</p>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">UUID</p>
            <p className="font-mono text-sm text-slate-900 break-all">{resultado.uuid || 'N/A'}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={onDescargarPDF} disabled={downloadingPDF}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              PDF
            </button>
            <button onClick={onDescargarXML} disabled={downloadingXML}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">
              {downloadingXML ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              XML
            </button>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Enviar por email</label>
            <div className="flex gap-2">
              <input type="email" value={emailEnvio} onChange={(e) => setEmailEnvio(e.target.value)}
                placeholder="correo@ejemplo.com"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500" />
              <button onClick={handleEnviar} disabled={!emailEnvio || enviando}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar
              </button>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-between">
          <button onClick={onVolverLista}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            Volver a lista
          </button>
          <button onClick={onNuevaFactura}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
            <Plus className="w-4 h-4" /> Crear otra factura
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Form View ====================

const FormView = ({
  emisor, emisorIncompleto,
  receptor, setReceptor,
  clientSearch, setClientSearch, clientResults, showClientDropdown, setShowClientDropdown, onSelectClient,
  conceptos, setConceptos, updateConcepto, removeConcepto, duplicateConcepto, toggleTaxes,
  onSATSearch, onUnitSearch,
  productSearch, setProductSearch, productResults, showProductDropdown, setShowProductDropdown, onSelectProduct,
  serie, setSerie, folio, setFolio,
  formaPago, setFormaPago, metodoPago, setMetodoPago,
  condicionesPago, setCondicionesPago, observaciones, setObservaciones,
  formasPago, metodosPago, regimenes, usosCFDI,
  totals, timbrando, onTimbrar, onCancelar
}) => {
  const productSearchRef = useRef(null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main form column */}
      <div className="lg:col-span-2 space-y-6">

        {/* Emisor */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" /> Emisor
          </h3>
          {emisorIncompleto ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Datos del emisor incompletos</p>
                <p className="text-sm text-amber-700 mt-1">
                  Completa RFC, Razón Social y CP en{' '}
                  <a href="/dashboard/configuracion" className="underline font-medium">Configuración</a>.
                </p>
              </div>
            </div>
          ) : emisor ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500">RFC</p>
                <p className="font-mono font-medium text-slate-900">{emisor.rfc}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Razón Social</p>
                <p className="font-medium text-slate-900 text-sm">{emisor.nombre}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Régimen Fiscal</p>
                <p className="font-medium text-slate-900">{emisor.regimen}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">CP Expedición</p>
                <p className="font-medium text-slate-900">{emisor.cp}</p>
              </div>
            </div>
          ) : null}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Serie</label>
              <input type="text" value={serie} onChange={(e) => setSerie(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm" maxLength={5} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Folio (auto si vacío)</label>
              <input type="text" value={folio} onChange={(e) => setFolio(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                placeholder="Automático" />
            </div>
          </div>
        </div>

        {/* Receptor */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-teal-600" /> Receptor
          </h3>

          {/* Client search */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
            <label className="block text-xs font-medium text-teal-800 mb-1">Buscar cliente guardado</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-400" />
              <input type="text" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Buscar por RFC o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)} />
              {showClientDropdown && clientResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {clientResults.map((client) => (
                    <button key={client.id} type="button"
                      onClick={() => onSelectClient(client)}
                      className="w-full text-left px-4 py-2 hover:bg-teal-50 transition-colors border-b border-slate-100 last:border-0 text-sm">
                      <span className="font-mono text-teal-700">{client.rfc}</span>
                      <span className="text-slate-600 ml-2">- {client.razon_social}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">RFC *</label>
              <input type="text" value={receptor.rfc}
                onChange={(e) => setReceptor({ ...receptor, rfc: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono uppercase text-sm"
                placeholder="XAXX010101000" maxLength={13} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Razón Social *</label>
              <input type="text" value={receptor.nombre}
                onChange={(e) => setReceptor({ ...receptor, nombre: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 uppercase text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Régimen Fiscal *</label>
              <select value={receptor.regimen_fiscal}
                onChange={(e) => setReceptor({ ...receptor, regimen_fiscal: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
                {regimenes.map(r => (
                  <option key={r.Value} value={r.Value}>{r.Value} - {r.Name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Uso CFDI *</label>
              <select value={receptor.uso_cfdi}
                onChange={(e) => setReceptor({ ...receptor, uso_cfdi: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
                {usosCFDI.map(u => (
                  <option key={u.Value} value={u.Value}>{u.Value} - {u.Name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Código Postal Fiscal *</label>
              <input type="text" value={receptor.domicilio_fiscal}
                onChange={(e) => setReceptor({ ...receptor, domicilio_fiscal: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                placeholder="00000" maxLength={5} pattern="[0-9]{5}" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
              <input type="email" value={receptor.email}
                onChange={(e) => setReceptor({ ...receptor, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                placeholder="cliente@ejemplo.com" />
            </div>
          </div>
        </div>

        {/* Conceptos */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-teal-600" /> Conceptos
          </h3>

          {/* Product search bar */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input ref={productSearchRef} type="text" value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Buscar producto del catálogo..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)} />
              {showProductDropdown && productResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {productResults.map((p) => (
                    <button key={p.id} type="button"
                      onClick={() => onSelectProduct(p)}
                      className="w-full text-left px-4 py-2.5 hover:bg-teal-50 transition-colors border-b border-slate-100 last:border-0">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.sku} | {p.sat_code || '01010101'}</p>
                        </div>
                        <span className="text-sm font-medium text-teal-600">
                          {formatCurrency(p.pricing?.retail_price || 0)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setConceptos(prev => [...prev, emptyConcepto()])}
              className="flex items-center gap-1 px-3 py-2 border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors text-sm whitespace-nowrap">
              <Plus className="w-4 h-4" /> Concepto libre
            </button>
          </div>

          {/* Conceptos list */}
          <div className="space-y-4">
            {conceptos.map((c, i) => (
              <ConceptoRow key={c._key}
                concepto={c} index={i}
                onUpdate={updateConcepto}
                onRemove={() => removeConcepto(i)}
                onDuplicate={() => duplicateConcepto(i)}
                onToggleTaxes={() => toggleTaxes(i)}
                onSATSearch={onSATSearch}
                onUnitSearch={onUnitSearch}
                canRemove={conceptos.length > 1}
              />
            ))}
          </div>
        </div>

        {/* Pago y Observaciones */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-teal-600" /> Pago y Observaciones
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Forma de Pago *</label>
              <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
                {formasPago.length > 0 ? formasPago.map(f => (
                  <option key={f.Value} value={f.Value}>{f.Value} - {f.Name}</option>
                )) : (
                  <>
                    <option value="01">01 - Efectivo</option>
                    <option value="03">03 - Transferencia electrónica</option>
                    <option value="04">04 - Tarjeta de crédito</option>
                    <option value="28">28 - Tarjeta de débito</option>
                    <option value="99">99 - Por definir</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Método de Pago *</label>
              <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
                {metodosPago.length > 0 ? metodosPago.map(m => (
                  <option key={m.Value} value={m.Value}>{m.Value} - {m.Name}</option>
                )) : (
                  <>
                    <option value="PUE">PUE - Pago en una sola exhibición</option>
                    <option value="PPD">PPD - Pago en parcialidades o diferido</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Condiciones de Pago</label>
              <input type="text" value={condicionesPago}
                onChange={(e) => setCondicionesPago(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                placeholder="Ej: Contado, 30 días, etc." />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Observaciones</label>
              <input type="text" value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                placeholder="Notas adicionales" />
            </div>
          </div>
        </div>

        {/* Mobile totals */}
        <div className="lg:hidden">
          <TotalsCard totals={totals} />
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button onClick={onCancelar}
            className="px-6 py-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button onClick={() => onTimbrar(false)} disabled={timbrando}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50">
            {timbrando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {timbrando ? 'Timbrando...' : 'Timbrar Factura'}
          </button>
          <button onClick={() => onTimbrar(true)} disabled={timbrando || !receptor.email}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            title={!receptor.email ? 'Ingresa email del receptor' : ''}>
            {timbrando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Timbrar y Enviar
          </button>
        </div>
      </div>

      {/* Sidebar: Totals */}
      <div className="hidden lg:block">
        <div className="sticky top-8">
          <TotalsCard totals={totals} />
        </div>
      </div>
    </div>
  );
};

// ==================== Totals Card ====================

const TotalsCard = ({ totals }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <h3 className="font-semibold text-slate-900 mb-4">Resumen</h3>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">Subtotal:</span>
        <span className="text-slate-900">{formatCurrency(totals.subtotal)}</span>
      </div>
      {totals.descuento > 0 && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">Descuento:</span>
          <span className="text-red-600">-{formatCurrency(totals.descuento)}</span>
        </div>
      )}
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">IVA traslado:</span>
        <span className="text-slate-900">+{formatCurrency(totals.iva)}</span>
      </div>
      {totals.ieps > 0 && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">IEPS:</span>
          <span className="text-slate-900">+{formatCurrency(totals.ieps)}</span>
        </div>
      )}
      {totals.retIva > 0 && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">Ret. IVA:</span>
          <span className="text-red-600">-{formatCurrency(totals.retIva)}</span>
        </div>
      )}
      {totals.retIsr > 0 && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">Ret. ISR:</span>
          <span className="text-red-600">-{formatCurrency(totals.retIsr)}</span>
        </div>
      )}
      <div className="border-t border-slate-200 pt-3 mt-3">
        <div className="flex justify-between text-lg font-bold">
          <span className="text-slate-900">Total:</span>
          <span className="text-teal-600">{formatCurrency(totals.total)}</span>
        </div>
      </div>
    </div>
  </div>
);

// ==================== Concepto Row ====================

const ConceptoRow = ({ concepto, index, onUpdate, onRemove, onDuplicate, onToggleTaxes, onSATSearch, onUnitSearch, canRemove }) => {
  const c = concepto;
  const lineSubtotal = c.cantidad * c.valor_unitario - (c.descuento || 0);

  return (
    <div className="border border-slate-200 rounded-lg p-4">
      {/* Main row */}
      <div className="grid grid-cols-12 gap-3 items-start">
        {/* Description */}
        <div className="col-span-12 md:col-span-4">
          <label className="block text-xs text-slate-500 mb-1">Descripción</label>
          <input type="text" value={c.descripcion}
            onChange={(e) => onUpdate(index, 'descripcion', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
            placeholder="Descripción del producto o servicio" />
        </div>
        {/* SAT Code */}
        <div className="col-span-6 md:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Clave SAT</label>
          <div className="relative">
            <input type="text" value={c._satSearch || c.clave_prod_serv}
              onChange={(e) => {
                onUpdate(index, 'clave_prod_serv', e.target.value);
                onSATSearch(index, e.target.value);
              }}
              onFocus={() => onUpdate(index, '_satSearch', c.clave_prod_serv)}
              onBlur={() => setTimeout(() => onUpdate(index, '_satResults', []), 200)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm font-mono" />
            {c._satResults && c._satResults.length > 0 && (
              <div className="absolute z-20 w-64 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {c._satResults.map((r, ri) => (
                  <button key={ri} type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onUpdate(index, 'clave_prod_serv', r.Value);
                      onUpdate(index, '_satSearch', '');
                      onUpdate(index, '_satResults', []);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-teal-50 text-xs border-b border-slate-100 last:border-0">
                    <span className="font-mono text-teal-700">{r.Value}</span> - {r.Name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Quantity */}
        <div className="col-span-3 md:col-span-1">
          <label className="block text-xs text-slate-500 mb-1">Cant.</label>
          <input type="number" value={c.cantidad} min="0.01" step="0.01"
            onChange={(e) => onUpdate(index, 'cantidad', parseFloat(e.target.value) || 0)}
            className="w-full px-2 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm text-center" />
        </div>
        {/* Unit Price */}
        <div className="col-span-5 md:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">P. Unitario</label>
          <input type="number" value={c.valor_unitario} min="0" step="0.01"
            onChange={(e) => onUpdate(index, 'valor_unitario', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm text-right" />
        </div>
        {/* Discount */}
        <div className="col-span-4 md:col-span-1">
          <label className="block text-xs text-slate-500 mb-1">Desc.</label>
          <input type="number" value={c.descuento} min="0" step="0.01"
            onChange={(e) => onUpdate(index, 'descuento', parseFloat(e.target.value) || 0)}
            className="w-full px-2 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm text-right" />
        </div>
        {/* Line total + actions */}
        <div className="col-span-8 md:col-span-2 flex items-end gap-1">
          <div className="flex-1">
            <label className="block text-xs text-slate-500 mb-1">Subtotal</label>
            <p className="px-3 py-2 bg-slate-50 rounded-lg text-sm font-medium text-slate-900 text-right">
              {formatCurrency(lineSubtotal)}
            </p>
          </div>
          <div className="flex gap-0.5 pb-0.5">
            <button onClick={onDuplicate} title="Duplicar"
              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
              <Copy className="w-3.5 h-3.5" />
            </button>
            {canRemove && (
              <button onClick={onRemove} title="Eliminar"
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tax toggle */}
      <button onClick={onToggleTaxes}
        className="mt-2 flex items-center gap-1 text-xs text-slate-500 hover:text-teal-600 transition-colors">
        {c._showTaxes ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {c._showTaxes ? 'Ocultar impuestos' : 'Configurar impuestos'}
        {(c.tasa_ieps > 0 || c.tasa_ret_iva > 0 || c.tasa_ret_isr > 0 || c.iva_exento) && (
          <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">Personalizado</span>
        )}
      </button>

      {/* Expanded tax section */}
      {c._showTaxes && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-6 gap-3">
          {/* Unit */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Unidad SAT</label>
            <div className="relative">
              <input type="text" value={c._unitSearch || c.clave_unidad}
                onChange={(e) => {
                  onUpdate(index, 'clave_unidad', e.target.value);
                  onUnitSearch(index, e.target.value);
                }}
                onFocus={() => onUpdate(index, '_unitSearch', c.clave_unidad)}
                onBlur={() => setTimeout(() => onUpdate(index, '_unitResults', []), 200)}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-mono" />
              {c._unitResults && c._unitResults.length > 0 && (
                <div className="absolute z-20 w-56 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {c._unitResults.map((r, ri) => (
                    <button key={ri} type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onUpdate(index, 'clave_unidad', r.Value);
                        onUpdate(index, 'unidad', r.Name);
                        onUpdate(index, '_unitSearch', '');
                        onUpdate(index, '_unitResults', []);
                      }}
                      className="w-full text-left px-3 py-1.5 hover:bg-teal-50 text-xs border-b border-slate-100 last:border-0">
                      <span className="font-mono text-teal-700">{r.Value}</span> - {r.Name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* IVA */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">IVA</label>
            <select value={c.iva_exento ? 'exento' : String(c.tasa_iva)}
              onChange={(e) => {
                if (e.target.value === 'exento') {
                  onUpdate(index, 'iva_exento', true);
                  onUpdate(index, 'tasa_iva', 0);
                } else {
                  onUpdate(index, 'iva_exento', false);
                  onUpdate(index, 'tasa_iva', parseFloat(e.target.value));
                }
              }}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs">
              <option value="0.16">16%</option>
              <option value="0.08">8%</option>
              <option value="0">0%</option>
              <option value="exento">Exento</option>
            </select>
          </div>
          {/* Objeto Imp */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Objeto Imp.</label>
            <select value={c.objeto_imp}
              onChange={(e) => onUpdate(index, 'objeto_imp', e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs">
              <option value="01">01 - No objeto</option>
              <option value="02">02 - Sí objeto</option>
              <option value="03">03 - Sí objeto, no causa</option>
            </select>
          </div>
          {/* IEPS */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">IEPS (tasa)</label>
            <input type="number" value={c.tasa_ieps} min="0" max="1" step="0.01"
              onChange={(e) => onUpdate(index, 'tasa_ieps', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs" />
          </div>
          {/* Ret IVA */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Ret. IVA</label>
            <input type="number" value={c.tasa_ret_iva} min="0" max="1" step="0.000001"
              onChange={(e) => onUpdate(index, 'tasa_ret_iva', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs"
              placeholder="0.106667" />
          </div>
          {/* Ret ISR */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Ret. ISR</label>
            <input type="number" value={c.tasa_ret_isr} min="0" max="1" step="0.01"
              onChange={(e) => onUpdate(index, 'tasa_ret_isr', parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs"
              placeholder="0.10" />
          </div>
        </div>
      )}
    </div>
  );
};

export default FacturacionManual;
